import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { HERO_PHOTOS, IMG_LAGOON, IMG_PALM_BEACH, PHOTO_MERMAID } from "@/lib/assets";
import { WORKSHOPS } from "@/data/workshops";
import {
  Sun, ArrowRight, ShieldCheck, Award, Users, Leaf, Compass, Palette,
  Sprout, Quote, Star, Calendar, Waves, Megaphone, CalendarClock,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UpcomingBanner() {
  const { lang } = useLang();
  const [ev, setEv] = useState(null);

  useEffect(() => {
    axios.get(`${API}/upcoming-event`).then((r) => setEv(r.data && r.data.title_fr ? r.data : null)).catch(() => setEv(null));
  }, []);

  if (!ev) return null;
  const title = lang === "fr" ? ev.title_fr : (ev.title_en || ev.title_fr);
  const dateLabel = lang === "fr" ? ev.date_label_fr : (ev.date_label_en || ev.date_label_fr);
  const text = lang === "fr" ? ev.text_fr : (ev.text_en || ev.text_fr);
  const ctaLabel = lang === "fr" ? ev.cta_label_fr : (ev.cta_label_en || ev.cta_label_fr);
  const isExternal = ev.cta_url && /^https?:\/\//.test(ev.cta_url);

  return (
    <section data-testid="upcoming-banner" className="relative overflow-hidden bg-[#2e5b6b]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#e0b36a] text-[#3a2f12] font-bold text-xs px-3 py-1.5 self-start shrink-0">
          <CalendarClock size={15} /> {lang === "fr" ? "À venir" : "Coming up"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-head font-bold text-white text-xl md:text-2xl leading-tight">{title}</p>
          {dateLabel && <p className="font-body font-semibold text-[#e0b36a] text-sm mt-0.5">{dateLabel}</p>}
          {text && <p className="font-body text-white/85 text-sm md:text-[15px] mt-1">{text}</p>}
        </div>
        {ev.cta_url && ctaLabel && (
          isExternal ? (
            <a href={ev.cta_url} target="_blank" rel="noreferrer" data-testid="upcoming-cta" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-sm px-6 py-3 shrink-0 transition-colors self-start md:self-auto">
              {ctaLabel} <ArrowRight size={16} />
            </a>
          ) : (
            <Link to={ev.cta_url} data-testid="upcoming-cta" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-sm px-6 py-3 shrink-0 transition-colors self-start md:self-auto">
              {ctaLabel} <ArrowRight size={16} />
            </Link>
          )
        )}
      </div>
    </section>
  );
}

function AnnouncementsSlider() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/announcements`).then((r) => setItems(r.data.announcements || [])).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(id);
  }, [items.length]);

  if (!items.length) return null;
  const a = items[idx % items.length];
  const title = lang === "fr" ? a.title_fr : (a.title_en || a.title_fr);
  const text = lang === "fr" ? a.text_fr : (a.text_en || a.text_fr);
  const ctaLabel = lang === "fr" ? a.cta_label_fr : (a.cta_label_en || a.cta_label_fr);
  const isExternal = a.cta_url && /^https?:\/\//.test(a.cta_url);

  return (
    <section data-testid="announcements-slider" className="relative bg-[#e0b36a] border-b border-[#cf9a4a]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#6f7d54] text-white font-bold text-xs px-3 py-1.5 shrink-0 self-start">
          <Megaphone size={14} /> {lang === "fr" ? "Annonce" : "News"}
        </span>
        <div key={a.id} className="flex-1 min-w-0 float-up">
          <p className="font-head font-bold text-[#3a2f12] text-base md:text-lg leading-tight">{title}</p>
          {text && <p className="font-body text-[#3a2f12]/85 text-sm md:text-[15px] mt-0.5">{text}</p>}
        </div>
        {a.cta_url && ctaLabel && (
          isExternal ? (
            <a href={a.cta_url} target="_blank" rel="noreferrer" data-testid="announcement-cta" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-sm px-5 py-2.5 shrink-0 transition-colors self-start sm:self-auto">
              {ctaLabel} <ArrowRight size={16} />
            </a>
          ) : (
            <Link to={a.cta_url} data-testid="announcement-cta" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-sm px-5 py-2.5 shrink-0 transition-colors self-start sm:self-auto">
              {ctaLabel} <ArrowRight size={16} />
            </Link>
          )
        )}
        {items.length > 1 && (
          <div className="flex gap-1.5 shrink-0">
            {items.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} data-testid={`announcement-dot-${i}`} className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-[#6f7d54]" : "w-2 bg-[#6f7d54]/40"}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Hero() {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % HERO_PHOTOS.length), 5000);
    return () => clearInterval(id);
  }, []);
  return (
    <section data-testid="hero" className="relative min-h-screen flex items-end overflow-hidden bg-[#6f7d54]">
      {HERO_PHOTOS.map((src, i) => (
        <div key={src} className={`absolute inset-0 transition-opacity duration-[1400ms] ${i === idx ? "opacity-100" : "opacity-0"}`}>
          <img src={src} alt="Island Camps Saint-Barthélemy" className={`w-full h-full object-cover ${i === idx ? "kenburns" : ""}`} />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-[#6f7d54] via-[#6f7d54]/45 to-[#6f7d54]/25" />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 lg:px-10 pb-24 pt-32">
        <div className="max-w-3xl float-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#e0b36a]/95 text-[#6f7d54] font-bold text-[13px] tracking-wide px-4 py-1.5 mb-6">
            <Sun size={15} /> {t.hero.kicker}
          </span>
          <h1 className="font-head font-extrabold text-white text-[2.5rem] leading-[1.05] sm:text-6xl lg:text-7xl tracking-tight whitespace-pre-line">
            {t.hero.title}
          </h1>
          <p className="font-body text-[#f7efe0]/90 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">{t.hero.subtitle}</p>
          <div className="flex flex-wrap items-center gap-4 mt-9">
            <Link to="/kids-camp" data-testid="hero-kidscamp-btn" className="inline-flex items-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-lg px-8 py-4 transition-all hover:-translate-y-0.5 shadow-lg">
              {t.hero.cta} <ArrowRight size={20} />
            </Link>
            <Link to="/adhesion" data-testid="hero-member-btn" className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white font-semibold text-lg px-7 py-4 hover:bg-white/10 transition-colors">
              {t.hero.ctaSecondary}
            </Link>
          </div>
        </div>
      </div>
      <div className="absolute bottom-7 right-7 z-10 hidden md:flex gap-2">
        {HERO_PHOTOS.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} data-testid={`hero-dot-${i}`} className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-[#e0b36a]" : "w-2 bg-white/50"}`} />
        ))}
      </div>
    </section>
  );
}

function Story() {
  const { t } = useLang();
  return (
    <section id="histoire" data-testid="story-section" className="grain relative bg-[#fbf6ec] py-24">
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="font-script text-[#e07a5f] text-3xl">{t.story.kicker}</span>
          <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-[2.6rem] leading-tight tracking-tight mt-1 mb-6">{t.story.title}</h2>
          <div className="space-y-4 font-body text-[#3a4d68] text-[17px] leading-relaxed">
            <p>{t.story.p1}</p>
            <p>{t.story.p2}</p>
            <p>{t.story.p3}</p>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-9">
            {t.story.stats.map((s, i) => (
              <div key={i} data-testid={`story-stat-${i}`} className="text-center bg-white/70 border border-[#6f7d54]/10 rounded-2xl py-5">
                <div className="font-head font-extrabold text-[#e07a5f] text-3xl">{s.value}</div>
                <div className="font-body text-[#3a4d68] text-xs mt-1 px-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <img src={PHOTO_MERMAID} alt="" className="w-full aspect-[4/5] object-cover rounded-[2rem] shadow-2xl rotate-1" />
          <img src={IMG_LAGOON} alt="" className="hidden md:block absolute -bottom-8 -left-8 w-44 h-44 object-cover rounded-3xl border-8 border-[#fbf6ec] shadow-xl -rotate-3" />
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  const { t } = useLang();
  const icons = [ShieldCheck, Award, Users, Leaf];
  return (
    <section data-testid="why-section" className="bg-[#f1e6d2] py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-6">
          <span className="font-script text-[#e07a5f] text-3xl">{t.whyChoose.kicker}</span>
          <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-5xl tracking-tight mt-1">{t.whyChoose.title}</h2>
        </div>
        <div className="flex justify-center mb-12">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#5e7d52]/12 text-[#4a6742] font-bold text-sm px-5 py-2.5 text-center">
            <ShieldCheck size={16} /> {t.whyChoose.badge}
          </span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.whyChoose.items.map((it, i) => {
            const Icon = icons[i];
            return (
              <div key={i} data-testid={`why-${i}`} className="bg-white rounded-3xl border border-[#6f7d54]/10 p-7 hover:-translate-y-1 hover:shadow-xl transition-all">
                <div className="w-12 h-12 rounded-2xl bg-[#e07a5f]/12 text-[#e07a5f] flex items-center justify-center mb-4"><Icon size={24} /></div>
                <h3 className="font-head font-bold text-[#6f7d54] text-lg mb-2">{it.title}</h3>
                <p className="font-body text-[#3a4d68]/90 text-[15px] leading-relaxed">{it.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Philosophy() {
  const { t } = useLang();
  const icons = [Compass, Palette, Users, Sprout];
  return (
    <section id="philosophie" data-testid="philosophy-section" className="relative bg-[#6f7d54] py-24 overflow-hidden">
      <img src={IMG_PALM_BEACH} alt="" className="absolute inset-0 w-full h-full object-cover opacity-12" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="max-w-2xl mb-14">
          <span className="font-script text-[#e0b36a] text-3xl">{t.philosophy.kicker}</span>
          <h2 className="font-head font-bold text-white text-3xl md:text-5xl tracking-tight mt-1 mb-4">{t.philosophy.title}</h2>
          <p className="font-body text-[#f7efe0]/80 text-lg">{t.philosophy.text}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.philosophy.steps.map((s, i) => {
            const Icon = icons[i];
            return (
              <div key={i} data-testid={`philosophy-${i}`} className="bg-white/8 border border-white/10 rounded-3xl p-7 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-xl bg-[#e0b36a]/20 text-[#e0b36a] flex items-center justify-center"><Icon size={20} /></span>
                  <span className="font-head font-extrabold text-[#e0b36a] text-2xl">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-head font-bold text-white text-xl mb-1.5">{s.title}</h3>
                <p className="font-body text-[#f7efe0]/75 text-[15px] leading-relaxed">{s.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SummerTeaser() {
  const { t } = useLang();
  return (
    <section id="ete" data-testid="summer-section" className="relative bg-[#fbf6ec] py-24 grain">
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative order-2 lg:order-1">
          <img src={IMG_LAGOON} alt="Lagon Grand Cul-de-Sac" className="w-full aspect-[4/3] object-cover rounded-[2rem] shadow-2xl" />
          <div className="absolute -top-5 -right-5 bg-[#e07a5f] text-white rounded-2xl px-5 py-3 font-head font-bold shadow-lg flex items-center gap-2 rotate-3"><Waves size={20} /> 2026–2027</div>
        </div>
        <div className="order-1 lg:order-2">
          <span className="font-script text-[#2e8b9e] text-3xl">{t.summer.kicker}</span>
          <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-5xl tracking-tight mt-1 mb-5 leading-tight">{t.summer.title}</h2>
          <p className="font-body text-[#3a4d68] text-lg leading-relaxed mb-8">{t.summer.text}</p>
          <Link to="/kids-camp" data-testid="summer-cta" className="inline-flex items-center gap-2 rounded-full bg-[#6f7d54] hover:bg-[#0f1c30] text-white font-bold text-lg px-8 py-4 transition-all hover:-translate-y-0.5">
            {t.summer.cta} <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function WorkshopsTeaser() {
  const { t, lang } = useLang();
  const w = t.workshops;
  const preview = WORKSHOPS.slice(0, 3);
  return (
    <section id="ateliers-teaser" data-testid="workshops-teaser" className="bg-[#f1e6d2] py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <span className="font-script text-[#e07a5f] text-3xl">{w.hero.kicker}</span>
            <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-5xl tracking-tight mt-1">{w.hero.title}</h2>
          </div>
          <Link to="/ateliers" data-testid="workshops-all-btn" className="inline-flex items-center gap-2 rounded-full border border-[#6f7d54]/20 text-[#6f7d54] font-bold px-6 py-3 hover:bg-white transition-colors self-start">
            {t.nav.workshops} <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {preview.map((ws, i) => (
            <Link to="/ateliers" key={ws.id} data-testid={`workshop-teaser-${i}`} className="group bg-white rounded-3xl overflow-hidden border border-[#6f7d54]/10 hover:-translate-y-1.5 hover:shadow-2xl transition-all">
              <div className="relative h-44 overflow-hidden">
                <img src={ws.img} alt={ws.title[lang]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-3 left-3 rounded-full bg-[#2e8b9e] text-white text-xs font-bold px-3 py-1 flex items-center gap-1"><Calendar size={12} /> {ws.day[lang]}</span>
              </div>
              <div className="p-6">
                <h3 className="font-head font-bold text-[#6f7d54] text-lg mb-2">{ws.title[lang]}</h3>
                <p className="font-body text-[#3a4d68]/90 text-sm">{ws.desc[lang]}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function AfterSchool() {
  const { t } = useLang();
  return (
    <section data-testid="afterschool-section" className="grain relative bg-[#fbf6ec] py-16">
      <div className="relative z-10 max-w-5xl mx-auto px-5 lg:px-10">
        <div className="bg-[#2e8b9e] rounded-[2rem] p-9 md:p-12 text-center text-white relative overflow-hidden">
          <span className="inline-block rounded-full bg-white/20 font-bold text-sm px-4 py-1.5 mb-4">{t.afterschool.badge}</span>
          <h2 className="font-head font-bold text-3xl md:text-4xl tracking-tight mb-3">{t.afterschool.title}</h2>
          <p className="font-body text-white/90 text-lg max-w-2xl mx-auto">{t.afterschool.text}</p>
        </div>
      </div>
    </section>
  );
}

function PricingMembership() {
  const { t } = useLang();
  const p = t.pricing;
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`).then((r) => setPricing(r.data)).catch(() => setPricing(null));
  }, []);
  const nm = pricing?.non_member_price ?? 65;
  const mb = pricing?.member_price ?? 45;
  const vol = pricing?.member_discount_price ?? 40;
  const membership = pricing?.membership_price ?? 75;
  const tiers = [
    { label: p.nonMember, price: `${nm}€`, popular: false },
    { label: p.member, price: `${mb}€`, popular: true },
    { label: p.memberVolume, price: `${vol}€`, popular: false },
  ];
  return (
    <section id="adhesion-section" data-testid="pricing-section" className="bg-[#f1e6d2] py-24">
      <div className="max-w-6xl mx-auto px-5 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="font-script text-[#e07a5f] text-3xl">{p.kicker}</span>
          <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-5xl tracking-tight mt-1">{p.title}</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-5 mb-8">
          {tiers.map((tier, i) => (
            <div key={i} data-testid={`price-tier-${i}`} className={`rounded-3xl p-7 text-center border transition-all ${tier.popular ? "bg-[#6f7d54] text-white border-[#6f7d54] scale-[1.03] shadow-2xl" : "bg-white text-[#6f7d54] border-[#6f7d54]/10"}`}>
              {tier.popular && <span className="inline-block rounded-full bg-[#e0b36a] text-[#6f7d54] font-bold text-xs px-3 py-1 mb-3">{p.badgePopular}</span>}
              <p className={`font-head font-semibold ${tier.popular ? "text-[#e0b36a]" : "text-[#3a4d68]"}`}>{tier.label}</p>
              <div className="font-head font-extrabold text-5xl my-2">{tier.price}</div>
              <p className={`font-body text-sm ${tier.popular ? "text-white/70" : "text-[#3a4d68]/70"}`}>{p.perSession}</p>
            </div>
          ))}
        </div>
        <p className="text-center font-body text-[#3a4d68] mb-7">{p.note}</p>
        <div className="text-center">
          <Link to="/adhesion" data-testid="pricing-cta" className="inline-flex items-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-lg px-8 py-4 transition-all hover:-translate-y-0.5">
            {p.cta} · {membership}€/an <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const { t } = useLang();
  return (
    <section data-testid="testimonials-section" className="grain relative bg-[#fbf6ec] py-24">
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="font-script text-[#e07a5f] text-3xl">{t.testimonials.kicker}</span>
          <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-5xl tracking-tight mt-1">{t.testimonials.title}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {t.testimonials.items.map((tm, i) => (
            <div key={i} data-testid={`testimonial-${i}`} className="bg-white rounded-3xl border border-[#6f7d54]/10 p-7 hover:shadow-xl transition-shadow">
              <Quote className="text-[#e0b36a]" size={32} />
              <div className="flex gap-0.5 my-3">{[...Array(5)].map((_, s) => <Star key={s} size={16} className="text-[#e0b36a]" fill="currentColor" />)}</div>
              <p className="font-body text-[#3a4d68] text-[15px] leading-relaxed mb-5">“{tm.quote}”</p>
              <div className="font-head font-bold text-[#6f7d54]">{tm.author}</div>
              <div className="font-body text-[#3a4d68]/70 text-sm">{tm.role}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function News() {
  const { t } = useLang();
  return (
    <section id="actualites" data-testid="news-section" className="bg-[#6f7d54] py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="max-w-2xl mb-12">
          <span className="font-script text-[#e0b36a] text-3xl">{t.news.kicker}</span>
          <h2 className="font-head font-bold text-white text-3xl md:text-5xl tracking-tight mt-1">{t.news.title}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {t.news.items.map((n, i) => (
            <article key={i} data-testid={`news-${i}`} className="bg-white/8 border border-white/10 rounded-3xl p-7 hover:bg-white/12 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <span className="rounded-full bg-[#e0b36a]/20 text-[#e0b36a] text-xs font-bold px-3 py-1">{n.tag}</span>
                <span className="font-body text-[#f7efe0]/50 text-sm">{n.date}</span>
              </div>
              <h3 className="font-head font-bold text-white text-lg mb-2 leading-snug">{n.title}</h3>
              <p className="font-body text-[#f7efe0]/75 text-[15px] leading-relaxed">{n.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const { t } = useLang();
  return (
    <section id="faq" data-testid="faq-section" className="grain relative bg-[#fbf6ec] py-24">
      <div className="relative z-10 max-w-3xl mx-auto px-5 lg:px-10">
        <div className="text-center mb-12">
          <span className="font-script text-[#e07a5f] text-3xl">{t.faq.kicker}</span>
          <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-5xl tracking-tight mt-1">{t.faq.title}</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {t.faq.items.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`} className="bg-white rounded-2xl border border-[#6f7d54]/10 px-5">
              <AccordionTrigger className="font-head font-semibold text-[#6f7d54] text-left hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="font-body text-[#3a4d68] text-[15px] leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function ContactBand() {
  const { t } = useLang();
  return (
    <section data-testid="contact-cta" className="relative bg-[#e07a5f] py-20 overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-5 text-center">
        <span className="font-script text-white/90 text-3xl">{t.contact.kicker}</span>
        <h2 className="font-head font-bold text-white text-3xl md:text-5xl tracking-tight mt-1 mb-4">{t.contact.title}</h2>
        <p className="font-body text-white/90 text-lg max-w-2xl mx-auto mb-8">{t.contact.text}</p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to="/adhesion" className="inline-flex items-center gap-2 rounded-full bg-white text-[#cf6a50] font-bold text-lg px-8 py-4 transition-all hover:-translate-y-0.5">{t.nav.member} <ArrowRight size={20} /></Link>
          <Link to="/reservation" className="inline-flex items-center gap-2 rounded-full border border-white/60 text-white font-semibold text-lg px-7 py-4 hover:bg-white/10 transition-colors">{t.nav.book}</Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <UpcomingBanner />
      <AnnouncementsSlider />
      <Story />
      <WhyChoose />
      <Philosophy />
      <SummerTeaser />
      <WorkshopsTeaser />
      <AfterSchool />
      <PricingMembership />
      <Testimonials />
      <News />
      <Faq />
      <ContactBand />
    </div>
  );
}
